<script setup>
// GK-HUB: Branded channel selector with official platform colors
import Icon from 'next/icon/Icon.vue';
import BrandedChannelSvg from 'next/icon/BrandedChannelSvg.vue';
import { computed } from 'vue';

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  description: {
    type: String,
    default: '',
  },
  icon: {
    type: String,
    required: true,
  },
  isComingSoon: {
    type: Boolean,
    default: false,
  },
});

// GK-HUB: Map icon class to branded channel type
const brandedChannel = computed(() => {
  const map = {
    'i-woot-whatsapp': 'whatsapp',
    'i-woot-instagram': 'instagram',
    'i-woot-telegram': 'telegram',
    'i-woot-messenger': 'messenger',
    'i-woot-line': 'line',
    'i-woot-tiktok': 'tiktok',
    'i-woot-mail': 'email',
    'i-woot-sms': null,
    'i-woot-website': 'website',
    'i-woot-api': null,
    'i-ri-phone-fill': null,
  };
  return map[props.icon] || null;
});

// GK-HUB: Background color per channel for the icon circle
const iconBgClass = computed(() => {
  const map = {
    whatsapp: 'bg-[#25D366]/10',
    instagram: 'bg-[#E1306C]/10',
    telegram: 'bg-[#26A5E4]/10',
    messenger: 'bg-[#0084FF]/10',
    line: 'bg-[#06C755]/10',
    tiktok: 'bg-n-alpha-2',
    email: 'bg-[#D14836]/10',
    website: 'bg-[#2E86C1]/10',
  };
  return map[brandedChannel.value] || 'bg-n-alpha-2';
});
</script>

<template>
  <button
    class="relative bg-n-solid-1 gap-6 cursor-pointer rounded-2xl flex flex-col justify-start transition-all duration-200 ease-in -m-px py-6 px-5 items-start border border-solid border-n-weak"
    :class="{
      'hover:enabled:border-n-blue-9 hover:enabled:shadow-md disabled:opacity-60 disabled:cursor-not-allowed':
        !isComingSoon,
      'cursor-not-allowed disabled:opacity-80': isComingSoon,
    }"
  >
    <!-- GK-HUB: Branded channel icons with official colors -->
    <div
      class="flex size-10 items-center justify-center rounded-full"
      :class="iconBgClass"
    >
      <BrandedChannelSvg
        v-if="brandedChannel"
        :channel="brandedChannel"
        size-class="size-6"
      />
      <!-- Fallback: original mono icon -->
      <Icon v-else :icon="icon" class="text-n-slate-10 size-6" />
    </div>
    <!-- GK-HUB: end branded channel icons -->

    <div class="flex flex-col items-start gap-1.5">
      <h3 class="text-n-slate-12 text-sm text-start font-medium capitalize">
        {{ title }}
      </h3>
      <p class="text-n-slate-11 text-start text-sm">
        {{ description }}
      </p>
    </div>

    <div
      v-if="isComingSoon"
      class="absolute inset-0 flex items-center justify-center backdrop-blur-[2px] rounded-2xl bg-gradient-to-br from-n-background/90 via-n-background/70 to-n-background/95 cursor-not-allowed"
    >
      <span class="text-n-slate-12 font-medium text-sm">
        {{ $t('CHANNEL_SELECTOR.COMING_SOON') }} 🚀
      </span>
    </div>
  </button>
</template>
